'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { getCategoryGradient } from '@loci/ui';
import type { EventDetailResponse } from '@/types/eventDetail';
import { CategoryIcon } from '@/components/atoms/CategoryIcon';
import { cn } from '@/components/atoms/primitives/utils';
import { buildEventImageSources } from '@/lib/image-proxy/eventImageSources';
import { EventTitleBlock } from './EventTitleBlock';

export interface EventDetailHeroProps {
  event: EventDetailResponse;
  /** Optional walk distance forwarded to the overlaid {@link EventTitleBlock}. */
  distance?: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string; strikethrough: boolean }> =
  {
    CANCELLED: { label: 'Cancelled', className: 'bg-red-500 text-white', strikethrough: true },
    POSTPONED: { label: 'Postponed', className: 'bg-orange-500 text-white', strikethrough: false },
    SOLD_OUT: { label: 'Sold Out', className: 'bg-amber-500 text-white', strikethrough: false },
  };

// LOCI hero fallback gradient — teal → deep navy, shipped inline so the hero
// renders without an event cover or a recognisable category.
const LOCI_HERO_GRADIENT =
  'linear-gradient(135deg, var(--loci-fixed-skyline) 0%, var(--loci-indigo) 100%)';

// Radial dot pattern overlay — 1px dots on a 16px grid, 12% opacity white.
// Inlined as a data-URI so it works without touching globals.css.
const DOT_PATTERN_SVG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'><circle cx='1' cy='1' r='1' fill='white' fill-opacity='0.12'/></svg>\")";

// Exported so the EventDetailTemplate can reuse the same formatting in the
// sticky context strip without drifting.
export function formatEventDate(
  date: string,
  endDate: string | null,
  timezone: string | null,
): string {
  const startDate = new Date(date);
  const endDateObj = endDate ? new Date(endDate) : null;

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  };

  const startStr = startDate.toLocaleString('en-US', options);

  if (endDate && endDateObj) {
    const startDay = startDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...(timezone ? { timeZone: timezone } : {}),
    });
    const endDay = endDateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...(timezone ? { timeZone: timezone } : {}),
    });

    if (startDay !== endDay) {
      const startShort = startDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        ...(timezone ? { timeZone: timezone } : {}),
      });
      const endShort = endDateObj.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        ...(timezone ? { timeZone: timezone } : {}),
      });
      return `${startShort} to ${endShort}`;
    }
  }

  return startStr;
}

/**
 * Resolve the hero background gradient from the event's primary category.
 * Falls back to the neutral LOCI teal → navy gradient when no category is set
 * or the slug isn't recognised by `getCategoryGradient`.
 */
function resolveHeroGradient(categorySlug: string | null | undefined): string {
  if (!categorySlug) return LOCI_HERO_GRADIENT;
  const resolved = getCategoryGradient(categorySlug);
  // `getCategoryGradient` returns an `unknown`-keyed fallback for misses.
  // Treat that as "no tint" and use the LOCI default so the hero always has a
  // known deep colour even if the token CSS hasn't mounted yet.
  if (resolved.includes('var(--loci-color-secondary)')) return LOCI_HERO_GRADIENT;
  return resolved;
}

export function EventDetailHero({ event, distance }: EventDetailHeroProps) {
  const { categories, status, imageUrl, imageUrls } = event;
  const statusConfig = STATUS_CONFIG[status];
  const heroGradient = resolveHeroGradient(categories[0]?.slug);
  const [imageFailed, setImageFailed] = useState(false);
  // Provider URL → same-origin proxy; unknown host → '' (category gradient).
  //
  // WEB-817: `next/image` builds its own `srcset` from whatever source it is
  // given, so there is no custom `srcset` to emit here — the win is feeding
  // the optimizer a right-sized INPUT. `large` (>=800px, capped at 2048 by
  // PIP-235) is the correct band for a 100vw hero and, being under the
  // optimizer's input ceiling, also sidesteps the PIP-148 `400`s the full-res
  // source produced. No `sizes` goes to the builder precisely because a
  // custom `srcset` would be ignored here.
  const { src: coverImageUrl } = buildEventImageSources({
    imageUrl,
    imageUrls,
    preferredBand: 'large',
  });
  const hasCover = Boolean(coverImageUrl) && !imageFailed;

  return (
    <section
      aria-label="Event hero"
      data-testid="event-detail-hero"
      className="relative w-full h-[460px] md:h-[380px] overflow-hidden"
      style={{ backgroundImage: heroGradient }}
    >
      {/* Cover image (optional) — sits behind the category gradient overlays.
          If the configured `next/image` `remotePatterns` doesn't include the
          URL's host, or the image 404s/times out, fall back to the category
          gradient by flipping `imageFailed` on error. */}
      {hasCover && coverImageUrl && (
        <Image
          src={coverImageUrl}
          alt=""
          fill
          priority
          sizes="100vw"
          aria-hidden="true"
          data-testid="hero-cover-image"
          className="object-cover z-0"
          onError={() => setImageFailed(true)}
        />
      )}

      {/* Category-tinted gradient wash — darkens the cover image and keeps the
          palette consistent when the image is missing or still loading. */}
      {hasCover && (
        <div
          aria-hidden="true"
          data-testid="hero-category-tint"
          className="absolute inset-0 z-10 opacity-55 mix-blend-multiply"
          // style-dynamic: gradient is resolved from the event's primary category
          style={{ backgroundImage: heroGradient }}
        />
      )}

      {/* Radial dot pattern overlay */}
      <div
        aria-hidden="true"
        data-testid="hero-dot-pattern"
        className="absolute inset-0 z-20"
        style={{ backgroundImage: DOT_PATTERN_SVG }}
      />

      {/* Bottom darken gradient for text legibility */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-20 bg-gradient-to-t from-black/60 via-black/20 to-transparent"
      />

      {/* Category icon watermark when no categories pill — subtle brand mark */}
      {categories.length === 0 && (
        <div
          data-testid="hero-placeholder"
          className="absolute inset-0 z-20 flex items-center justify-center"
        >
          <CategoryIcon iconName={null} size={64} className="text-white/20" />
        </div>
      )}

      {/* Status pill — top-right */}
      {statusConfig && (
        <div
          data-testid="status-pill"
          aria-label={`Event ${statusConfig.label.toLowerCase()}`}
          className={cn(
            'absolute top-4 right-4 z-30 px-3 py-1 rounded-full text-xs font-semibold',
            statusConfig.className,
          )}
        >
          {statusConfig.label}
        </div>
      )}

      <EventTitleBlock
        event={event}
        distance={distance}
        strikethrough={Boolean(statusConfig?.strikethrough)}
      />
    </section>
  );
}
